/**
 * E2E renderer test probe — drives the REAL desktop-only feature gates on the
 * standalone-web branch and reports whether they threw, via
 * `globalThis.__irisWebFeatureGateProbe`.
 *
 * WHY a probe is required here (read before replacing it with a UI interaction):
 * the gates branch on `isElectronApp()`, which is computed ONCE at module load from
 * `window.electron` (electron/utils.ts) and cached. The Playwright e2e suite only ever
 * drives an Electron renderer, where that flag is permanently true, so no click path
 * inside the suite can reach the browser branch. The gate entry points therefore take
 * the surface as an explicit trailing parameter (defaulted to the resolver, never
 * passed by product code), and this probe supplies the standalone-web value.
 *
 * What the probe does NOT fake: the gate body, the guidance emit, `track()`,
 * envelope enrichment, the before_send chain, and the ingest POST are all the real
 * production path. The ONLY injected value is the surface constant — the surface
 * RESOLUTION itself is covered by the jest suites
 * (analytics/domains/feature-gate.test.ts, ipjoystick/network.test.ts).
 *
 * Per `think-in-systems` (ai.knowledge/global): a primitive. It exposes the gate
 * entry points, not a re-implementation of the emit.
 * Per `stay-in-scope` (ai.knowledge/global): only the two entry points the ENG-4332
 * receipt asserts on. No additional methods.
 *
 * Mirrors calibration-probe.ts exactly: same `window.electron.process.env` gate (bare
 * `process.env` is EMPTY in this renderer — see that file's long comment), same
 * single-statement global assignment, same test-only intent.
 */
import { ENVELOPE_CLIENT, DESKTOP_ONLY_FEATURE } from '#~/analytics/constants/event-properties.js';
import { getNetworkInfo } from '#~/ipjoystick/network.js';
import { ipControllerManager } from '#~/ipjoystick/comm.js';
import { reportDesktopOnlyFeatureGate } from '#~/analytics/domains/feature-gate.js';

/** What a driven gate did, in a shape Playwright can assert on directly. */
interface GateOutcome {
  /** True if the gate rejected. The whole point of ENG-4332 is that it must be false. */
  threw: boolean;
  /** The rejection message when it threw, so a regression names itself in the report. */
  error?: string;
  /** `typeof` the resolved value — the gate must resolve, not reject, on web. */
  returned: string;
}

interface WebFeatureGateProbe {
  openIpControllerManagement(): Promise<GateOutcome>;
  launchIpController(): Promise<GateOutcome>;
  reportLocalModeGate(): boolean;
}

// Renderer-process gate. See calibration-probe.ts for why this reads through the
// preload bridge rather than bare `process.env`.
const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

const runGate = async (gate: () => Promise<unknown>): Promise<GateOutcome> => {
  try {
    const returned = await gate();
    return { threw: false, returned: typeof returned };
  } catch (error) {
    return {
      threw: true,
      error: error instanceof Error ? error.message : String(error),
      returned: 'never'
    };
  }
};

if (isE2eEnabled()) {
  const probe: WebFeatureGateProbe = {
    /**
     * The ticket's flagship path: opening IP-controller management reads the local
     * IP/port. Pre-ENG-4332 this threw "No network interfaces found" on web, uncaught,
     * and posthog-js autocaptured it as an `$exception`.
     */
    openIpControllerManagement: () => runGate(() => getNetworkInfo(ENVELOPE_CLIENT.STANDALONE_WEB)),

    /**
     * The write half of the same feature: spawning the VISCA bridge process.
     * Pre-ENG-4332 this threw ">> Failed to launch Visca Bridge: No context or pid".
     * The address/port are placeholders — the gate closes before any IPC is attempted.
     */
    launchIpController: () =>
      runGate(() =>
        ipControllerManager.launchViscaBridge(
          '127.0.0.1',
          52381,
          'e2e-web-gate-probe',
          '',
          ENVELOPE_CLIENT.STANDALONE_WEB
        )
      ),

    /**
     * A second, different feature through the same emitter — proves the signal is
     * per-feature rather than hard-coded to the ip-controller family.
     */
    reportLocalModeGate: () =>
      reportDesktopOnlyFeatureGate(DESKTOP_ONLY_FEATURE.LOCAL_MODE, ENVELOPE_CLIENT.STANDALONE_WEB)
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisWebFeatureGateProbe = probe;
}
